import React, { useCallback, useEffect, useState } from 'react';
import {
  InternalApiAlertDecisionRule,
  InternalApiAlertDecisionRuleCommand,
  InternalApiEntityType,
} from '../../api-internal';
import Table from '../../table/Table';
import {
  DecisionRuleEditingHeader,
  DecisionRuleEditingRow,
  DecisionRuleHeader,
  DecisionRuleRow,
} from './DecisionRuleRow';
import Has from '../../auth/Has';
import { SalvButton, Size } from '@salvapp/ui-components';
import { useAlertStatuses } from '../../screening/alert-status/alertStatusesHelper';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { decisionRulesApi } from '../../apis';

interface DecisionRuleTableProps {
  rules: Array<InternalApiAlertDecisionRule>;
  type: InternalApiEntityType;
}

const DecisionRuleTable = ({ rules, type }: DecisionRuleTableProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [, allStatuses] = useAlertStatuses();

  const [relevantRules, setRelevantRules] = useState<InternalApiAlertDecisionRule[]>([]);
  const resetRules = useCallback(() => {
    setRelevantRules(rules.filter((rule) => rule.entityType === type));
  }, [rules, type]);
  useEffect(() => {
    resetRules();
  }, [rules, resetRules]);

  const updateRule = useCallback(
    (rule: InternalApiAlertDecisionRule | null, index: number) => {
      if (rule === null) {
        const newRules = [...relevantRules];
        newRules.splice(index, 1);
        setRelevantRules(newRules);
      } else {
        const newRules = [...relevantRules];
        newRules[index] = rule;
        setRelevantRules(newRules);
      }
    },
    [relevantRules]
  );

  const addNewRule = useCallback(() => {
    setRelevantRules((relevantRules) => [
      ...relevantRules,
      {
        sortOrder: 0,
        name: '',
        runOnlyOnFinal: false,
        entityType: type,
        runCommand: InternalApiAlertDecisionRuleCommand.ALL_ARE,
        values: [],
      },
    ]);
  }, []);

  const saveRules = useCallback(async () => {
    const rules = await decisionRulesApi.updateDecisionRules({
      internalApiCreateAlertDecisionRulesCommand: {
        entityType: type,
        rules: relevantRules,
      },
    });
    setRelevantRules(rules);
  }, [relevantRules, type]);

  return (
    <Table
      hover={false}
      className='decision-rule-table'
      title={`${type} decision rules`}
      total={relevantRules.length}
      page={0}
      size={relevantRules.length}
      items={relevantRules}
      fetchPage={() => {
        // empty
      }}
      actions={() => (
        <Has allPermissions='CAN_MODIFY_DECISION_RULES'>
          {!isEditing && (
            <SalvButton
              onClick={() => {
                setIsEditing(true);
              }}
              size={Size.SMALL}
              label={'Edit Rules'}
              variant={'muted'}
            />
          )}
          {isEditing && (
            <>
              <SalvButton
                onClick={() => {
                  addNewRule();
                }}
                size={Size.SMALL}
                iconLeft={faPlus}
                className='m-r-8'
                label={'Add rule'}
                variant={'muted'}
              />
              <SalvButton
                onClick={() => {
                  setIsEditing(false);
                  resetRules();
                }}
                size={Size.SMALL}
                className='m-r-8'
                label={'Cancel'}
                variant={'muted'}
              />
              <SalvButton
                onClick={() => {
                  setIsEditing(false);
                  saveRules();
                }}
                size={Size.SMALL}
                label={'Save'}
                variant={'danger'}
              />
            </>
          )}
        </Has>
      )}
      header={() => (isEditing ? DecisionRuleEditingHeader() : DecisionRuleHeader())}
      body={(rules) =>
        rules.map((rule, i) => {
          if (isEditing) {
            return (
              <DecisionRuleEditingRow
                key={i}
                rule={rule}
                ruleIndex={i}
                statuses={allStatuses}
                updateRule={updateRule}
              />
            );
          } else {
            return <DecisionRuleRow key={i} rule={rule} statuses={allStatuses} />;
          }
        })
      }
    />
  );
};

export default DecisionRuleTable;
